/**
 * A frozen account (its team's contract ended) is rejected by the API with
 * `{code: "account_frozen"}`: 401 on any JWT route or refresh, 403 on login.
 *
 * Such a rejection is final — refreshing cannot fix it — so the interceptor
 * logs out without trying, and leaves a flag behind for the login screen to
 * explain WHY the user is back there. sessionStorage: it survives the redirect
 * and reloads in this tab, and dies with the tab.
 */
import axios from 'axios';

export const ACCOUNT_FROZEN = 'account_frozen';
export const SERVICE_UNAVAILABLE = 'service_unavailable';

const FLAG_KEY = 'accountFrozen';

/** A 401/403 whose body says the account is frozen. */
export function isAccountFrozenError(error: unknown): boolean {
    if (!axios.isAxiosError(error) || !error.response) return false;
    const {status, data} = error.response;
    return (status === 401 || status === 403) && (data as any)?.code === ACCOUNT_FROZEN;
}

export function markAccountFrozen(): void {
    try {
        sessionStorage.setItem(FLAG_KEY, '1');
    } catch {
        // Blocked storage: the login toast still explains it.
    }
}

/** Read-only, so a StrictMode double render cannot swallow it. */
export function isAccountFrozenFlagged(): boolean {
    try {
        return sessionStorage.getItem(FLAG_KEY) === '1';
    } catch {
        return false;
    }
}

/** Cleared by a successful login. */
export function clearAccountFrozen(): void {
    try {
        sessionStorage.removeItem(FLAG_KEY);
    } catch {
        // ignore
    }
}
